/* ==========================================================================
   Plant Store <> UnfoldCRO — shared add-to-cart

   One implementation for every custom "add" in the theme (product card, bundle
   builder, seed box), because the drawer refresh has three details that are
   easy to miss and were each getting re-invented:

     1. `sections` must be a comma-joined STRING and must be paired with
        `sections_url`, or Shopify renders the drawer against the wrong URL
        context and the markup comes back stale.
     2. renderContents() only swaps #CartDrawer's innerHTML, so the `is-empty`
        flag Dawn keeps on the <cart-drawer> HOST survives — a freshly filled
        cart then renders inside its empty layout. It has to be cleared first.
     3. renderContents() opens the drawer itself; calling open() as well
        double-fires the transition.

   Resolves with the parsed response, or rejects — callers own their own button
   states.
   ========================================================================== */
(function () {
  'use strict';

  if (window.ufcCartAddBound) return;
  window.ufcCartAddBound = true;

  function refreshCartCount() {
    return fetch(window.Shopify.routes.root + 'cart.js', {
      headers: { Accept: 'application/json' }
    })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (cart) {
        if (!cart) return;
        var count = String(cart.item_count);
        var source = document.querySelector('#cart-icon-bubble [data-ufc-cart-count]');
        if (source) {
          source.textContent = count;
          source.hidden = cart.item_count === 0;
        }
        Array.prototype.forEach.call(
          document.querySelectorAll('[data-ufc-cart-mirror]'),
          function (target) {
            target.textContent = count;
            target.hidden = cart.item_count === 0;
          }
        );
      })
      .catch(function () {});
  }

  var lastSlide = 0;

  function ufcCartHandledByApp() {
    return typeof window.slideShiprocketSmartCartInFrame === 'function';
  }

  function ufcOpenCart() {
    refreshCartCount();
    if (!ufcCartHandledByApp()) return false;
    var now = Date.now();
    if (now - lastSlide > 900) {
      lastSlide = now;
      try {
        window.slideShiprocketSmartCartInFrame();
      } catch (e) {
        return false;
      }
    }
    return true;
  }

  var ADD_URL = /\/cart\/add(\.js)?(\?|$)/;
  var MUTATE_URL = /\/cart\/(change|update|clear)(\.js)?(\?|$)/;

  function isAdd(url, method) {
    return ADD_URL.test(String(url || '')) && String(method || 'GET').toUpperCase() === 'POST';
  }

  function isMutate(url, method) {
    return MUTATE_URL.test(String(url || '')) && String(method || 'GET').toUpperCase() === 'POST';
  }

  function wrapMiniCart(fn) {
    if (typeof fn !== 'function' || fn.ufcWrapped) return fn;
    var wrapped = function () {
      var result = fn.apply(this, arguments);
      if (result && typeof result.then === 'function') {
        return result.then(function (value) {
          refreshCartCount();
          return value;
        });
      }
      refreshCartCount();
      return result;
    };
    wrapped.ufcWrapped = true;
    return wrapped;
  }

  function watchMiniCart() {
    var held = window.updateMiniCartSection;
    try {
      Object.defineProperty(window, 'updateMiniCartSection', {
        configurable: true,
        get: function () {
          return held;
        },
        set: function (fn) {
          held = wrapMiniCart(fn);
        },
      });
    } catch (e) {
      held = null;
    }
    if (typeof held === 'function') held = wrapMiniCart(held);

    var tries = 0;
    var poll = window.setInterval(function () {
      var live = window.updateMiniCartSection;
      if (typeof live === 'function' && !live.ufcWrapped) window.updateMiniCartSection = live;
      if (++tries > 60) window.clearInterval(poll);
    }, 300);
  }

  function watchCartAdds() {
    var nativeFetch = window.fetch;
    if (typeof nativeFetch === 'function') {
      window.fetch = function (input, init) {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        var method = (init && init.method) || (input && input.method) || 'GET';
        var watching = isAdd(url, method);
        var mutating = isMutate(url, method);
        return nativeFetch.apply(this, arguments).then(function (response) {
          if (response && response.ok) {
            if (watching) ufcOpenCart();
            else if (mutating) refreshCartCount();
          }
          return response;
        });
      };
    }

    var nativeOpen = XMLHttpRequest.prototype.open;
    var nativeSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.ufcCartAdd = isAdd(url, method);
      this.ufcCartMutate = isMutate(url, method);
      return nativeOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      if (this.ufcCartAdd || this.ufcCartMutate) {
        var self = this;
        this.addEventListener('load', function () {
          if (self.status < 200 || self.status >= 300) return;
          if (self.ufcCartAdd) ufcOpenCart();
          else refreshCartCount();
        });
      }
      return nativeSend.apply(this, arguments);
    };
  }

  watchCartAdds();
  watchMiniCart();

  /**
   * @param {Array<{id: (string|number), quantity: number}>} items
   * @returns {Promise<Object>}
   */
  function ufcAddToCart(items) {
    if (!items || !items.length) return Promise.reject(new Error('no items'));

    var drawer = document.querySelector('cart-drawer');
    var canRender = !!(drawer && typeof drawer.renderContents === 'function');

    var sectionIds = ['cart-icon-bubble'];
    if (canRender && typeof drawer.getSectionsToRender === 'function') {
      sectionIds = drawer.getSectionsToRender().map(function (section) {
        return section.id;
      });
    }

    return fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: items.map(function (item) {
          return { id: Number(item.id), quantity: item.quantity || 1 };
        }),
        sections: sectionIds.join(','),
        sections_url: window.location.pathname
      })
    })
      .then(function (response) {
        if (!response.ok) throw new Error(response.status);
        return response.json();
      })
      .then(function (data) {
        if (ufcCartHandledByApp()) return data;
        if (canRender && data && data.sections && drawer.isConnected) {
          drawer.classList.remove('is-empty');
          drawer.renderContents(data);
        }
        return data;
      });
  }

  window.ufcAddToCart = ufcAddToCart;
  window.ufcRefreshCartCount = refreshCartCount;
  window.ufcOpenCart = ufcOpenCart;
  window.ufcCartHandledByApp = ufcCartHandledByApp;
})();
